import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "en" | "de" | "fr";

export const LANGS: { code: Lang; label: string }[] = [
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
  { code: "fr", label: "FR" },
];

type Dict = Record<string, string>;

const dicts: Record<Lang, Dict> = {
  en: {
    "nav.join": "Join Waitlist",
    "nav.close": "Close",
    "hero.title": "Your path to health begins with a thorough diagnosis",
    "hero.sub": "Take the expert survey designed by our specialists and receive a personalized treatment plan based on your symptoms, goals, and tests.",
    "hero.cta.join": "Join Waitlist",
    "hero.cta.survey": "Take the survey",
    "problem.title": "More than just a list of supplements",
    "problem.sub": "You receive an interactive health map: justification for each dosage, intake schedule, and forecast of changes in your biomarkers.",
    "problem.c1": "General recommendations that don't consider your tests",
    "problem.c2": "You search for vitamins for hours and still aren't sure",
    "problem.c3": "Dozens of bottles that don't work in combination",
    "plan.title": "Personalized plan — no extra effort",
    "plan.sub": "From analysis to a ready-made kit in a few simple steps",
    "plan.s1.title": "Upload your test results",
    "plan.s1.body": "Drop in your bloodwork — PDF or photo. We parse dozens of biomarkers instantly.",
    "plan.s2.title": "AI creates a personalized plan",
    "plan.s2.body": "Our medical AI cross-references your data with Swiss clinical protocols to design dosage, timing, and stacks.",
    "plan.s3.title": "Receive your kit — everything labeled and packed",
    "plan.s3.body": "Sealed sachets, morning/evening routines, delivered to your door every month.",
    "inside.title": "Appointment schedule: before meals / after meals / morning / evening — just like a real doctor",
    "inside.sub": "Start with a proven database or create a custom profile under expert supervision.",
    "inside.i1.title": "AI fine-tuned specifically for nutrition and analysis",
    "inside.i1.desc": "A medical model trained on clinical nutrition data and lab panels — not a general chatbot.",
    "inside.i2.title": "Accounts for allergens, pregnancy, current medication and deficiencies",
    "inside.i2.desc": "Every recommendation is filtered through your individual health context.",
    "inside.i3.title": "Intake schedule: before meals / after meals / morning / evening — like a real doctor",
    "inside.i3.desc": "Each supplement is scheduled for the right moment of your day.",
    "inside.i4.title": "Everything packed into clean boxes, each sachet labeled",
    "inside.i4.desc": "No loose bottles. Daily sachets, ready to grab and go.",
    "inside.i5.title": "Don't need deep personalization? Universal Day / Night formula",
    "inside.i5.desc": "A ready-made foundation for those who want the essentials only.",
    "inside.i6.title": "If needed, connect with a doctor",
    "inside.i6.desc": "Talk to a specialist the moment you have questions about your plan.",
    "trust.title": "Trusted by us",
    "trust.sub": "A few quiet markers behind every recommendation.",
    "trust.accuracy": "time-tested accuracy",
    "trust.i1.title": "Swiss quality and precision",
    "trust.i1.desc": "Manufactured and validated under Swiss clinical standards.",
    "trust.i2.title": "Our own AI, trained specifically for this",
    "trust.i2.desc": "Not a general model — tuned on nutrition science and lab panels.",
    "trust.i3.title": "AI + doctors — together, not one instead of the other",
    "trust.i3.desc": "Every plan is AI-generated and medically reviewed.",
    "trust.i4.title": "Your data is protected",
    "trust.i4.desc": "End-to-end encryption. Your health data never leaves your vault.",
    "faq.title": "FAQ",
    "faq.sub": "Everything you wanted to ask about our personalized approach",
    "cta.title": "See Beyond. Unlock Your Health",
    "cta.sub": "Your body holds the answers — we help you see them",
    "cta.btn": "Join the waitlist",
    "footer.title": "Your path to health begins with a thorough diagnosis",
    "footer.contact": "Contact us",
    "survey.question": "Question",
    "survey.of": "of",
    "survey.back": "Back",
    "survey.next": "Continue",
    "survey.submit": "Submit",
    "survey.thanks.title": "Thank you",
    "survey.thanks.sub": "Your answers help us shape Alvion. We'll notify you when we launch.",
    "survey.home": "Back to home",
    "wl.title": "Join the Waitlist",
    "wl.sub": "Be the first to access Alvion. We'll reach out with early access details.",
    "wl.first": "First name",
    "wl.last": "Last name",
    "wl.email": "Email",
    "wl.phone": "Phone (optional)",
    "wl.submit": "Request early access",
    "wl.done.title": "You're in",
    "wl.done.sub": "We'll be in touch soon with next steps.",
    "cookie.title": "We use cookies",
    "cookie.body": "We use essential cookies to run this site and optional analytics cookies to improve it. You can decline — everything still works.",
    "cookie.accept": "Accept all",
    "cookie.decline": "Decline",
  },
  de: {
    "nav.join": "Warteliste",
    "nav.close": "Schliessen",
    "hero.title": "Ihr Weg zur Gesundheit beginnt mit einer gründlichen Diagnose",
    "hero.sub": "Beantworten Sie unseren Expertenfragebogen und erhalten Sie einen personalisierten Behandlungsplan basierend auf Ihren Symptomen, Zielen und Laborwerten.",
    "hero.cta.join": "Auf die Warteliste",
    "hero.cta.survey": "Umfrage starten",
    "problem.title": "Mehr als nur eine Liste von Nahrungsergänzungen",
    "problem.sub": "Sie erhalten eine interaktive Gesundheitskarte: Begründung jeder Dosierung, Einnahmeplan und Prognose Ihrer Biomarker.",
    "problem.c1": "Allgemeine Empfehlungen, die Ihre Laborwerte nicht berücksichtigen",
    "problem.c2": "Sie suchen stundenlang nach Vitaminen und sind immer noch unsicher",
    "problem.c3": "Dutzende Fläschchen, die nicht zusammen wirken",
    "plan.title": "Personalisierter Plan — ohne Mehraufwand",
    "plan.sub": "Von der Analyse zum fertigen Set in wenigen Schritten",
    "plan.s1.title": "Laborwerte hochladen",
    "plan.s1.body": "Laden Sie Ihre Blutwerte hoch — PDF oder Foto. Wir lesen dutzende Biomarker sofort aus.",
    "plan.s2.title": "KI erstellt Ihren persönlichen Plan",
    "plan.s2.body": "Unsere medizinische KI verknüpft Ihre Daten mit Schweizer klinischen Protokollen — Dosierung, Timing, Kombination.",
    "plan.s3.title": "Sie erhalten Ihr Set — beschriftet und verpackt",
    "plan.s3.body": "Versiegelte Sachets, Morgen-/Abend-Routinen, monatlich bis vor die Haustür.",
    "inside.title": "Einnahmeplan: vor dem Essen / nach dem Essen / morgens / abends — wie beim echten Arzt",
    "inside.sub": "Starten Sie mit einer bewährten Datenbank oder erstellen Sie ein individuelles Profil unter fachlicher Aufsicht.",
    "inside.i1.title": "KI speziell für Ernährung und Laboranalysen trainiert",
    "inside.i1.desc": "Ein medizinisches Modell, trainiert auf klinischen Daten — kein allgemeiner Chatbot.",
    "inside.i2.title": "Berücksichtigt Allergene, Schwangerschaft, Medikamente und Defizite",
    "inside.i2.desc": "Jede Empfehlung wird durch Ihren persönlichen Gesundheitskontext gefiltert.",
    "inside.i3.title": "Einnahmeplan: vor / nach dem Essen, morgens / abends — wie beim Arzt",
    "inside.i3.desc": "Jedes Präparat wird genau zum richtigen Zeitpunkt eingeplant.",
    "inside.i4.title": "Alles in hübsche Boxen verpackt, jedes Sachet beschriftet",
    "inside.i4.desc": "Keine losen Fläschchen. Tages-Sachets, zum Mitnehmen.",
    "inside.i5.title": "Keine Tiefenpersonalisierung nötig? Universelle Tag/Nacht-Formel",
    "inside.i5.desc": "Eine fertige Basis für alle, die nur das Wesentliche möchten.",
    "inside.i6.title": "Bei Bedarf — direkter Kontakt zum Arzt",
    "inside.i6.desc": "Sprechen Sie mit einem Spezialisten, sobald Fragen aufkommen.",
    "trust.title": "Vertrauen Sie uns",
    "trust.sub": "Leise Marker hinter jeder Empfehlung.",
    "trust.accuracy": "geprüfte Genauigkeit",
    "trust.i1.title": "Schweizer Qualität und Präzision",
    "trust.i1.desc": "Hergestellt und validiert nach Schweizer klinischen Standards.",
    "trust.i2.title": "Eigene KI, speziell darauf trainiert",
    "trust.i2.desc": "Kein allgemeines Modell — auf Ernährungswissenschaft und Laborwerte abgestimmt.",
    "trust.i3.title": "KI + Ärzte — gemeinsam, nicht eins statt des anderen",
    "trust.i3.desc": "Jeder Plan wird von KI erstellt und medizinisch geprüft.",
    "trust.i4.title": "Ihre Daten sind geschützt",
    "trust.i4.desc": "Ende-zu-Ende-Verschlüsselung. Ihre Gesundheitsdaten bleiben bei Ihnen.",
    "faq.title": "FAQ",
    "faq.sub": "Alles, was Sie zum personalisierten Ansatz wissen möchten",
    "cta.title": "Sieh weiter. Entdecke deine Gesundheit",
    "cta.sub": "Ihr Körper hat die Antworten — wir helfen Ihnen, sie zu sehen",
    "cta.btn": "Auf die Warteliste",
    "footer.title": "Ihr Weg zur Gesundheit beginnt mit einer gründlichen Diagnose",
    "footer.contact": "Kontakt",
    "survey.question": "Frage",
    "survey.of": "von",
    "survey.back": "Zurück",
    "survey.next": "Weiter",
    "survey.submit": "Absenden",
    "survey.thanks.title": "Vielen Dank",
    "survey.thanks.sub": "Ihre Antworten helfen uns, Alvion zu gestalten. Wir melden uns beim Start.",
    "survey.home": "Zurück zur Startseite",
    "wl.title": "Auf die Warteliste",
    "wl.sub": "Seien Sie unter den Ersten. Wir informieren Sie über den Early-Access.",
    "wl.first": "Vorname",
    "wl.last": "Nachname",
    "wl.email": "E-Mail",
    "wl.phone": "Telefon (optional)",
    "wl.submit": "Early Access anfragen",
    "wl.done.title": "Sie sind dabei",
    "wl.done.sub": "Wir melden uns mit den nächsten Schritten.",
    "cookie.title": "Wir verwenden Cookies",
    "cookie.body": "Wir nutzen notwendige Cookies für den Betrieb der Seite und optionale Analyse-Cookies zur Verbesserung. Sie können ablehnen — alles funktioniert weiterhin.",
    "cookie.accept": "Alle akzeptieren",
    "cookie.decline": "Ablehnen",
  },
  fr: {
    "nav.join": "Liste d'attente",
    "nav.close": "Fermer",
    "hero.title": "Votre chemin vers la santé commence par un diagnostic approfondi",
    "hero.sub": "Répondez au questionnaire conçu par nos spécialistes et recevez un plan personnalisé selon vos symptômes, objectifs et analyses.",
    "hero.cta.join": "Rejoindre la liste",
    "hero.cta.survey": "Commencer le questionnaire",
    "problem.title": "Bien plus qu'une simple liste de compléments",
    "problem.sub": "Vous recevez une carte santé interactive : justification des dosages, calendrier de prise et prévision de vos biomarqueurs.",
    "problem.c1": "Des recommandations génériques qui ignorent vos analyses",
    "problem.c2": "Vous cherchez des vitamines pendant des heures sans certitude",
    "problem.c3": "Des dizaines de flacons qui ne s'accordent pas",
    "plan.title": "Plan personnalisé — sans effort supplémentaire",
    "plan.sub": "De l'analyse au kit prêt à l'emploi en quelques étapes",
    "plan.s1.title": "Téléchargez vos analyses",
    "plan.s1.body": "Déposez vos résultats — PDF ou photo. Nous lisons des dizaines de biomarqueurs instantanément.",
    "plan.s2.title": "L'IA crée votre plan personnalisé",
    "plan.s2.body": "Notre IA médicale croise vos données avec les protocoles cliniques suisses — dosage, timing, associations.",
    "plan.s3.title": "Recevez votre kit — étiqueté et emballé",
    "plan.s3.body": "Sachets scellés, routine matin/soir, livrés à domicile chaque mois.",
    "inside.title": "Horaires de prise : avant / après les repas / matin / soir — comme un vrai médecin",
    "inside.sub": "Commencez avec une base éprouvée ou créez un profil sur mesure sous supervision d'expert.",
    "inside.i1.title": "IA spécialement entraînée sur la nutrition et les analyses",
    "inside.i1.desc": "Un modèle médical formé sur des données cliniques — pas un chatbot généraliste.",
    "inside.i2.title": "Prend en compte allergènes, grossesse, médicaments et carences",
    "inside.i2.desc": "Chaque recommandation passe par votre contexte de santé personnel.",
    "inside.i3.title": "Horaires de prise : avant / après repas, matin / soir — comme un médecin",
    "inside.i3.desc": "Chaque complément est planifié au bon moment de la journée.",
    "inside.i4.title": "Tout emballé en jolies boîtes, chaque sachet étiqueté",
    "inside.i4.desc": "Plus de flacons en vrac. Des sachets quotidiens prêts à emporter.",
    "inside.i5.title": "Pas besoin de personnalisation poussée ? Formule Jour / Nuit",
    "inside.i5.desc": "Une base prête à l'emploi pour ceux qui veulent l'essentiel.",
    "inside.i6.title": "Si besoin — contact direct avec un médecin",
    "inside.i6.desc": "Parlez à un spécialiste dès que vous avez une question.",
    "trust.title": "La confiance, prouvée",
    "trust.sub": "Quelques repères discrets derrière chaque recommandation.",
    "trust.accuracy": "précision éprouvée",
    "trust.i1.title": "Qualité et précision suisses",
    "trust.i1.desc": "Fabriqué et validé selon les standards cliniques suisses.",
    "trust.i2.title": "Notre propre IA, spécialement entraînée pour cela",
    "trust.i2.desc": "Pas un modèle généraliste — ajusté sur la nutrition et les analyses.",
    "trust.i3.title": "IA + médecins — ensemble, pas l'un à la place de l'autre",
    "trust.i3.desc": "Chaque plan est généré par l'IA et validé médicalement.",
    "trust.i4.title": "Vos données sont protégées",
    "trust.i4.desc": "Chiffrement de bout en bout. Vos données santé restent à vous.",
    "faq.title": "FAQ",
    "faq.sub": "Tout ce que vous vouliez savoir sur notre approche personnalisée",
    "cta.title": "Voyez au-delà. Libérez votre santé",
    "cta.sub": "Votre corps a les réponses — nous vous aidons à les voir",
    "cta.btn": "Rejoindre la liste",
    "footer.title": "Votre chemin vers la santé commence par un diagnostic approfondi",
    "footer.contact": "Contactez-nous",
    "survey.question": "Question",
    "survey.of": "sur",
    "survey.back": "Retour",
    "survey.next": "Continuer",
    "survey.submit": "Envoyer",
    "survey.thanks.title": "Merci",
    "survey.thanks.sub": "Vos réponses nous aident à façonner Alvion. Nous vous contacterons au lancement.",
    "survey.home": "Retour à l'accueil",
    "wl.title": "Rejoindre la liste",
    "wl.sub": "Soyez parmi les premiers. Nous vous contacterons pour l'accès anticipé.",
    "wl.first": "Prénom",
    "wl.last": "Nom",
    "wl.email": "E-mail",
    "wl.phone": "Téléphone (optionnel)",
    "wl.submit": "Demander l'accès anticipé",
    "wl.done.title": "Vous êtes inscrit",
    "wl.done.sub": "Nous revenons vers vous avec la suite.",
    "cookie.title": "Nous utilisons des cookies",
    "cookie.body": "Cookies essentiels pour le fonctionnement et cookies d'analyse optionnels pour améliorer le site. Vous pouvez refuser — tout continue de fonctionner.",
    "cookie.accept": "Tout accepter",
    "cookie.decline": "Refuser",
  },
};

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: string) => string;
};

const I18nCtx = createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "en";
    const saved = localStorage.getItem("alvion.lang") as Lang | null;
    if (saved && dicts[saved]) return saved;
    const nav = navigator.language.slice(0, 2).toLowerCase();
    if (nav === "de" || nav === "fr") return nav;
    return "en";
  });
  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem("alvion.lang", l);
  };
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (k: string) => dicts[lang][k] ?? dicts.en[k] ?? k;
  return <I18nCtx.Provider value={{ lang, setLang, t }}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const c = useContext(I18nCtx);
  if (!c) throw new Error("useI18n outside provider");
  return c;
}
